import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { requireFeature } from "@/lib/feature-gate";
import {
  getTaskStatusOptions,
  taskStatusListSchema,
} from "@/lib/task-status-options";
import type { TaskStatus } from "@/types/task";

// Filters live in the URL so they survive refresh and can be shared/bookmarked.
// Off-contract values degrade to undefined instead of failing the route.
const taskSearchSchema = z.object({
  status: taskStatusListSchema,
  source: z.enum(["crm", "all"]).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  taskId: z.number().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/_layout/tasks")({
  beforeLoad: () => requireFeature("tasks"),
  validateSearch: taskSearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  // AdminTaskViewSet's list/retrieve need `task.task.view`; every other
  // action (create/update/destroy/start/complete/submit) needs
  // `task.task.manage`. Either one admits the page; the page itself gates
  // write actions down to `task.task.manage` holders only.
  return (
    <ProtectedRoute requiredPermission={["task.task.view", "task.task.manage"]}>
      <TaskDataTable />
    </ProtectedRoute>
  );
}

import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import { Button } from "@/components/ui/button";

//Task Hooks
import { type TaskOrdering } from "@/hooks/api/tasks/useFetchTask";
import type { AdminCreateTaskPayload, Task } from "@/types/task";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SelectUser from "@/components/reusable-components/select-user";
import { useDebounce } from "@/hooks/use-debounce";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import useFetchTasks from "@/hooks/api/tasks/useFetchTask";
import useFetchTaskById from "@/hooks/api/tasks/useFetchTaskById";
import useCreateTask from "@/hooks/api/tasks/useCreateTask";
import useUpdateTask from "@/hooks/api/tasks/useUpdateTask";
import useDeleteTask from "@/hooks/api/tasks/useDeleteTask";
import { taskKeys } from "@/hooks/api/tasks/keys";
import { useCrmTaskMutations } from "@/hooks/api/crm/useCrmTasks";
import { TaskTable } from "@/components/crud/table/TaskTable";
import { TaskKanbanBoard } from "@/components/crud/table/TaskKanbanBoard";
import { TaskFormSheet } from "@/components/crud/forms/TaskFormSheet";
import { CompleteTaskDialog } from "@/components/crud/forms/CompleteTaskDialog";
import { CrmTaskFormSheet } from "@/components/crm/forms/CrmTaskFormSheet";
import { SubtaskFormSheet } from "@/components/crud/forms/SubtaskFormSheet";
import { TaskReviewSheet } from "@/components/crud/forms/TaskReviewSheet";

// Subtask Hooks
import useCreateSubtask from "@/hooks/api/tasks/subtask/useCreateSubtask";
import useUpdateSubtask from "@/hooks/api/tasks/subtask/useUpdateSubtask";
import useDeleteSubtask from "@/hooks/api/tasks/subtask/useDeleteSubtask";
import type {
  AdminSubtaskPayload,
  Subtask,
  SubtaskPayload,
} from "@/types/subtask";
import useStartTask from "@/hooks/api/tasks/useStartTask";
import useCompleteTask from "@/hooks/api/tasks/useCompleteTask";
import useStartSubtask from "@/hooks/api/tasks/subtask/useStartSubtask";
import useCompleteSubtask from "@/hooks/api/tasks/subtask/useCompleteSubtask";
import useApproveTask from "@/hooks/api/tasks/approval/useApproveTask";
import useRejectTask from "@/hooks/api/tasks/approval/useRejectTask";
import useBulkApprove from "@/hooks/api/tasks/approval/useBulkApprove";
import useSubmitTask from "@/hooks/api/tasks/approval/useSubmitTask";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

import { Check, LayoutGrid, List } from "lucide-react";

function TaskDataTable() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Task hooks (query built further below, once filters/pagination state exists)
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  // Subtask hooks
  const createSubtask = useCreateSubtask();
  const updateSubtask = useUpdateSubtask();
  const deleteSubtask = useDeleteSubtask();

  // Start and Complete Task Hooks
  const startTask = useStartTask();
  const completeTask = useCompleteTask();
  const startSubtask = useStartSubtask();
  const completeSubtask = useCompleteSubtask();

  const approveTask = useApproveTask();
  const rejectTask = useRejectTask();
  const bulkApproveTask = useBulkApprove();
  const submitTask = useSubmitTask();

  const { updateCrmTask } = useCrmTaskMutations();
  // Errors surface via each mutation's meta.errorMessage (global toast) —
  // no manual error-state effect needed anymore.

  // Filter and Search
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        title: SortDirection;
        created_at: SortDirection;
        due_date: SortDirection;
        status: SortDirection;
        priority: SortDirection;
      },
      TaskOrdering
    >({
      title: null,
      created_at: null,
      due_date: null,
      status: null,
      priority: null,
    });

  const taskApprovalRequired = useOrgSettingsStore(
    (s) => s.settings?.task_approval_required ?? true
  );
  const { data: capabilities } = useMyPermissions();
  const canApproveTasks = can(capabilities, "task.task.approve");
  const canManageTasks = can(capabilities, "task.task.manage");

  const searchParams = Route.useSearch();
  const statusFilter = searchParams.status;
  const sourceFilter = searchParams.source ?? "hr";
  const setStatusFilter = useCallback(
    (values: TaskStatus[]) =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          status: values.length > 0 ? values : undefined,
        }),
        replace: true,
      }),
    [navigate]
  );
  const setSourceFilter = useCallback(
    (value: "hr" | "crm" | "all") =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          source: value === "hr" ? undefined : value,
        }),
        replace: true,
      }),
    [navigate]
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.q ?? "");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);

  // Persist the (debounced) search text in the URL alongside the dropdown filters.
  useEffect(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, navigate]);

  // React Hook Form for user filter
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  // UI state — tasks
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [taskToComplete, setTaskToComplete] = useState<{
    id: number;
    title?: string;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // CRM task sheet
  const [isCrmSheetOpen, setIsCrmSheetOpen] = useState(false);
  const [editingCrmTask, setEditingCrmTask] = useState<Task | null>(null);

  // UI state — subtasks
  const [isSubtaskSheetOpen, setIsSubtaskSheetOpen] = useState(false);
  const [isSubtaskViewOnly, setIsSubtaskViewOnly] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);
  const [parentTask, setParentTask] = useState<Task | null>(null);
  const [subtaskToDelete, setSubtaskToDelete] = useState<{
    subtask: Subtask;
    taskId: number;
  } | null>(null);
  const [isSubtaskDeleteDialogOpen, setIsSubtaskDeleteDialogOpen] =
    useState(false);

  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [showBulkApproveConfirm, setShowBulkApproveConfirm] = useState(false);
  const [isReviewSheetOpen, setIsReviewSheetOpen] = useState(false);
  const [taskToReview, setTaskToReview] = useState<Task | null>(null);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const KANBAN_PAGE_SIZE = 100;

  const tasksQuery = useFetchTasks(
    viewMode === "kanban"
      ? {
          page: 1,
          page_size: KANBAN_PAGE_SIZE,
          search: debouncedSearch || undefined,
          status_in: statusFilter?.length ? statusFilter : undefined,
          source: sourceFilter === "all" ? undefined : sourceFilter,
        }
      : {
          page,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          status_in: statusFilter?.length ? statusFilter : undefined,
          ordering: getApiOrdering(),
          source: sourceFilter === "all" ? undefined : sourceFilter,
        },
    true
  );
  const tasks = tasksQuery.data?.results ?? [];
  const count = tasksQuery.data?.count ?? 0;

  const isMutating =
    createTask.isPending ||
    updateTask.isPending ||
    deleteTask.isPending ||
    createSubtask.isPending ||
    updateSubtask.isPending ||
    deleteSubtask.isPending ||
    startTask.isPending ||
    completeTask.isPending ||
    startSubtask.isPending ||
    completeSubtask.isPending ||
    approveTask.isPending ||
    rejectTask.isPending ||
    bulkApproveTask.isPending ||
    submitTask.isPending ||
    updateCrmTask.isPending;
  const isLoading = tasksQuery.isPending || isMutating;
  const isFetching = tasksQuery.isFetching || isMutating;

  // For resetting the filters panel back to defaults
  const resetFilters = () => {
    setOrdering({
      title: null,
      created_at: null,
      due_date: null,
      status: null,
      priority: null,
    });
    navigate({ to: ".", search: {}, replace: true });
    setSearchQuery("");
    resetDebounce();
    setPage(1);
    setValue("user_id", undefined);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, sourceFilter, viewMode, setPage]);

  // Task handlers
  const handleCreate = () => {
    setEditingTask(null);
    setEditingTaskId(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTask(task);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEditCrmTask = (task: Task) => {
    setEditingCrmTask(task);
    setIsCrmSheetOpen(true);
  };

  const handleCrmTaskEditSubmit = async (id: number, data: Partial<import("@/types/crm").CrmTaskPayload>) => {
    await updateCrmTask.mutateAsync({ id, payload: data });
    toast.success("CRM task updated successfully!");
    setIsCrmSheetOpen(false);
    setEditingCrmTask(null);
    // updateCrmTask self-invalidates the CRM domain's own query keys, but
    // this unified tasks list (source: "crm" rows included) is read from
    // the HR tasks endpoint/cache (taskKeys), which knows nothing about the
    // CRM cache — it needs its own explicit nudge to reflect the edit.
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
  };

  const handleView = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTask(task);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  // Deep-link from a notification: ?taskId=<id> opens the view-only sheet.
  // Clearing it drops only taskId so the URL-persisted filters survive.
  const deepLinkTaskId = searchParams.taskId ?? null;
  const clearDeepLink = () =>
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, taskId: undefined }),
      replace: true,
    });
  const deepLinkQuery = useFetchTaskById(deepLinkTaskId, true, {
    enabled: deepLinkTaskId != null,
  });
  useEffect(() => {
    if (deepLinkTaskId == null) return;
    if (deepLinkQuery.data) {
      handleView(deepLinkQuery.data);
    } else if (deepLinkQuery.isError) {
      clearDeepLink();
    }
    // handleView is stable enough for this one-shot open; deps track the id/data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTaskId, deepLinkQuery.data, deepLinkQuery.isError]);

  const handleCloseViewSheet = () => {
    setIsSheetOpen(false);
    if (deepLinkTaskId != null) {
      clearDeepLink();
    }
  };

  const handleDeleteClick = (task: Task) => {
    setTaskToDelete(task);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask.mutateAsync({ id: taskToDelete.id, isAdmin: true });
      toast.success("Task deleted successfully!");
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: AdminCreateTaskPayload) => {
    try {
      if (editingTaskId) {
        await updateTask.mutateAsync({
          id: editingTaskId,
          payload: data,
          isAdmin: true,
        });
        toast.success("Task updated successfully!");
      } else {
        await createTask.mutateAsync({ payload: data, isAdmin: true });
        toast.success("Task created successfully!");
      }
      setIsSheetOpen(false);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Subtask handlers
  const handleAddSubtask = (task: Task) => {
    setParentTask(task);
    setEditingSubtask(null);
    setIsSubtaskViewOnly(false);
    setIsSubtaskSheetOpen(true);
  };

  const handleViewSubtask = (subtask: Subtask, taskId: number) => {
    setParentTask(tasks.find((t) => t.id === taskId) ?? null);
    setEditingSubtask(subtask);
    setIsSubtaskViewOnly(true);
    setIsSubtaskSheetOpen(true);
  };

  const handleEditSubtask = (subtask: Subtask, taskId: number) => {
    setParentTask(tasks.find((t) => t.id === taskId) ?? null);
    setEditingSubtask(subtask);
    setIsSubtaskViewOnly(false);
    setIsSubtaskSheetOpen(true);
  };

  const handleDeleteSubtaskClick = (subtask: Subtask, taskId: number) => {
    setSubtaskToDelete({ subtask, taskId });
    setIsSubtaskDeleteDialogOpen(true);
  };

  const handleDeleteSubtaskConfirm = async () => {
    if (!subtaskToDelete) return;
    try {
      await deleteSubtask.mutateAsync({
        taskId: subtaskToDelete.taskId,
        id: subtaskToDelete.subtask.id,
        isAdmin: true,
      });
      toast.success("Subtask deleted successfully!");
      setIsSubtaskDeleteDialogOpen(false);
      setSubtaskToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleSubtaskFormSubmit = async (data: SubtaskPayload) => {
    if (!parentTask) return;
    const payload: AdminSubtaskPayload = { ...data, task: parentTask.id };
    try {
      if (editingSubtask) {
        await updateSubtask.mutateAsync({
          taskId: parentTask.id,
          id: editingSubtask.id,
          payload,
          isAdmin: true,
        });
        toast.success("Subtask updated successfully!");
      } else {
        await createSubtask.mutateAsync({
          taskId: parentTask.id,
          payload,
          isAdmin: true,
        });
        toast.success("Subtask created successfully!");
      }
      setIsSubtaskSheetOpen(false);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleStartTaskById = async (id: number) => {
    try {
      await startTask.mutateAsync({ id, isAdmin: true });
      toast.success("Task started successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };
  const handleStartTask = (task: Task) => handleStartTaskById(task.id);

  const handleSubmitTask = async (task: Task) => {
    try {
      await submitTask.mutateAsync({ id: task.id, isAdmin: true });
      toast.success("Task submitted successfully!");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "";
      if (msg.toLowerCase().includes("not required")) {
        try {
          await startTask.mutateAsync({ id: task.id, isAdmin: true });
          toast.success("Task started successfully!");
        } catch {
          // error toast comes from the mutation's meta.errorMessage
        }
      }
    }
  };

  // Completion opens a dialog so the user can record what was done.
  // async so it satisfies the sheet's `(id) => Promise<void>` prop type.
  const handleCompleteTaskById = async (id: number, title?: string) => {
    setTaskToComplete({ id, title });
  };
  const handleCompleteTask = (task: Task) =>
    handleCompleteTaskById(task.id, task.title);

  const handleCompleteConfirm = async (remarks: string) => {
    if (!taskToComplete) return;
    await completeTask.mutateAsync({
      id: taskToComplete.id,
      isAdmin: true,
      payload: { completion_remarks: remarks },
    });
    toast.success("Task completed successfully!");
    setTaskToComplete(null);
  };

  const handleReviewTask = (taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setTaskToReview(task);
      setIsReviewSheetOpen(true);
    }
  };

  const handleApproveConfirm = async (id: number, remarks: string) => {
    try {
      await approveTask.mutateAsync({ id, payload: { remarks } });
      toast.success("Task approved successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleRejectConfirm = async (id: number, remarks: string) => {
    try {
      await rejectTask.mutateAsync({ id, payload: { remarks } });
      toast.success("Task rejected successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTaskIds.length === 0) return;
    try {
      const result = await bulkApproveTask.mutateAsync({
        task_ids: selectedTaskIds,
      });
      // Some ids may be skipped -- e.g. your own task, which nobody may
      // approve themselves.
      if (result.skipped.length > 0) {
        toast.warning(
          `Approved ${result.approved.length} of ${selectedTaskIds.length} tasks.`,
          {
            description: [...new Set(result.skipped.map((s) => s.reason))].join(
              " ",
            ),
          },
        );
      } else {
        toast.success("Tasks approved successfully!");
      }
      setSelectedTaskIds([]);
      setShowBulkApproveConfirm(false);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleStartSubtaskById = async (id: number, taskId: number) => {
    try {
      await startSubtask.mutateAsync({ taskId, id, isAdmin: true });
      toast.success("Subtask started successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };
  const handleStartSubtask = (subtask: Subtask, taskId: number) =>
    handleStartSubtaskById(subtask.id, taskId);

  const handleCompleteSubtaskById = async (id: number, taskId: number) => {
    try {
      await completeSubtask.mutateAsync({
        taskId,
        id,
        isAdmin: true,
      });
      toast.success("Subtask completed successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };
  const handleCompleteSubtask = (subtask: Subtask, taskId: number) =>
    handleCompleteSubtaskById(subtask.id, taskId);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  //autocomplete users
  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Tasks"
          description="Manage, assign, and track employee task efficiently"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManageTasks ? handleCreate : undefined}
          addLabel="Create Task"
          isLoading={isFetching}
        >
          {taskApprovalRequired &&
            canApproveTasks &&
            selectedTaskIds.length > 0 && (
            <Button
              onClick={() => setShowBulkApproveConfirm(true)}
              disabled={isFetching}
              variant={"secondary"}
            >
              <Check className="h-4 w-4" />
              Bulk Approve ({selectedTaskIds.length})
            </Button>
          )}
          <div className="flex items-center rounded-md border overflow-hidden">
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("kanban")}
              title="Kanban view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={users}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />

          <SelectFilter
            placeholder="Source"
            options={[
              { label: "Normal", value: "hr" },
              { label: "All", value: "all" },
              { label: "CRM", value: "crm" },
            ]}
            value={sourceFilter}
            onChange={setSourceFilter}
          />

          <MultiSelectFilter
            placeholder="Status"
            options={getTaskStatusOptions({ taskApprovalRequired })}
            values={statusFilter ?? []}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        {viewMode === "table" ? (
          <>
            <TaskTable
              tasks={tasks}
              onTitleClick={(id) =>
                navigate({ to: "/admin/tasks/$id", params: { id: String(id) } })
              }
              onView={handleView}
              onEdit={handleEdit}
              onEditCrmTask={handleEditCrmTask}
              onDelete={handleDeleteClick}
              onAddSubtask={handleAddSubtask}
              onViewSubtask={handleViewSubtask}
              onEditSubtask={handleEditSubtask}
              onDeleteSubtask={handleDeleteSubtaskClick}
              onStartTask={handleStartTask}
              onCompleteTask={handleCompleteTask}
              onStartSubtask={handleStartSubtask}
              onCompleteSubtask={handleCompleteSubtask}
              isLoading={isLoading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
              onReviewTask={handleReviewTask}
              canApprove={canApproveTasks}
              canManage={canManageTasks}
              onSubmitTask={handleSubmitTask}
              selectedTaskIds={selectedTaskIds}
              onSelectionChange={setSelectedTaskIds}
              showSourceColumn={sourceFilter === "all" || sourceFilter === "crm"}
              onUserClick={(userId) =>
                navigate({
                  to: "/admin/users/$id",
                  params: { id: String(userId) },
                  search: { tab: "tasks" },
                })
              }
            />

            <PaginationComponent
              count={count}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              isLoading={isFetching}
            />
          </>
        ) : (
          <TaskKanbanBoard
            tasks={tasks}
            taskApprovalRequired={taskApprovalRequired}
            includeDeferred={sourceFilter !== "hr"}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            onEditCrmTask={handleEditCrmTask}
            onDelete={handleDeleteClick}
            onStartTask={handleStartTask}
            onSubmitTask={handleSubmitTask}
            onCompleteTask={handleCompleteTask}
            onReviewTask={handleReviewTask}
            canApprove={canApproveTasks}
            canManage={canManageTasks}
          />
        )}

        <TaskFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseViewSheet}
          onSubmit={handleFormSubmit}
          users={users}
          onSearchUsers={handleSearchUsers}
          editingTask={editingTask}
          isLoading={isLoading}
          onStartTask={handleStartTaskById}
          onCompleteTask={(id) => handleCompleteTaskById(id)}
          onStartSubtask={(id) => handleStartSubtaskById(id, parentTask?.id || 0)}
          onCompleteSubtask={(id) =>
            handleCompleteSubtaskById(id, parentTask?.id || 0)
          }
          onUpdateSubtaskStatus={async (taskId, subtask, status) => {
            if (status === "in_progress")
              await handleStartSubtask(subtask, taskId);
            else if (status === "completed")
              await handleCompleteSubtask(subtask, taskId);
          }}
          actionLoading={isMutating}
        />

        <CrmTaskFormSheet
          isOpen={isCrmSheetOpen}
          onClose={() => {
            setIsCrmSheetOpen(false);
            setEditingCrmTask(null);
          }}
          onCreate={async () => {}}
          onEdit={handleCrmTaskEditSubmit}
          editingTask={editingCrmTask}
          isLoading={updateCrmTask.isPending}
        />

        <SubtaskFormSheet
          isAdmin={true}
          isOpen={isSubtaskSheetOpen}
          isViewOnly={isSubtaskViewOnly}
          onClose={() => setIsSubtaskSheetOpen(false)}
          onSubmit={handleSubtaskFormSubmit}
          editingSubtask={editingSubtask}
          parentTaskTitle={parentTask?.title}
          isLoading={isLoading}
        />

        <CompleteTaskDialog
          isOpen={!!taskToComplete}
          onClose={() => setTaskToComplete(null)}
          taskTitle={taskToComplete?.title}
          isLoading={completeTask.isPending}
          onConfirm={handleCompleteConfirm}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Task"
          description={`Are you sure you want to delete this task for "${taskToDelete?.user}" with the title "${taskToDelete?.title}"? This action cannot be undone.`}
          itemName={taskToDelete?.title}
          isLoading={isLoading}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={isSubtaskDeleteDialogOpen}
          onClose={() => setIsSubtaskDeleteDialogOpen(false)}
          onConfirm={handleDeleteSubtaskConfirm}
          title="Delete Subtask"
          description={`Are you sure you want to delete the subtask "${subtaskToDelete?.subtask.title}"? This action cannot be undone.`}
          itemName={subtaskToDelete?.subtask.title}
          isLoading={isLoading}
          destructive
        />

        <TaskReviewSheet
          isOpen={isReviewSheetOpen}
          onClose={() => setIsReviewSheetOpen(false)}
          isLoading={isLoading}
          task={taskToReview}
          onApprove={handleApproveConfirm}
          onReject={handleRejectConfirm}
        />

        <ConfirmDialog
          isOpen={showBulkApproveConfirm}
          onClose={() => setShowBulkApproveConfirm(false)}
          onConfirm={handleBulkApprove}
          isLoading={bulkApproveTask.isPending}
          title="Bulk Approve Tasks"
          description={`Approve ${selectedTaskIds.length} selected task(s)? This action cannot be undone.`}
          confirmLabel="Approve All"
          loadingLabel="Approving..."
        />
      </div>
    </SidebarInset>
  );
}
